import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Send, Plus, Eye, Pause, Calendar, Target, Filter, CheckCircle, Clock, AlertCircle, X, Upload } from 'lucide-react';
import { whatsappService } from '../../../../services/whatsappService';


const BulkMessaging = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [campaignForm, setCampaignForm] = useState({
    campaign_name: '',
    template_id: '',
    target_audience: {
      role: 'patient',
      active: true,
      age_min: null,
      age_max: null,
      conditions: []
    },
    scheduled_at: '',
    send_immediately: true
  });

  const audienceOptions = [
    { value: 'all_patients', label: 'All Patients', description: 'Send to all active patients', icon: Users },
    { value: 'new_patients', label: 'New Patients', description: 'Patients registered in last 30 days', icon: Users },
    { value: 'upcoming_appointments', label: 'Upcoming Appointments', description: 'Patients with appointments this week', icon: Calendar },
    { value: 'psoriasis_patients', label: 'Psoriasis Patients', description: 'Patients with psoriasis treatment history', icon: Target },
    { value: 'pcod_patients', label: 'PCOD Patients', description: 'Patients with PCOD/PCOS treatment', icon: Target },
    { value: 'arthritis_patients', label: 'Arthritis Patients', description: 'Patients with arthritis conditions', icon: Target },
    { value: 'custom', label: 'Custom Audience', description: 'Define custom filtering criteria', icon: Filter }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      const [campaignsResult, templatesResult] = await Promise.all([
        whatsappService?.getBulkCampaigns(),
        whatsappService?.getMessageTemplates()
      ]);

      setCampaigns(campaignsResult?.data || []);
      setTemplates(templatesResult?.data?.filter(t => t?.is_active) || []);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const campaignData = {
        ...campaignForm,
        target_audience: JSON.stringify(campaignForm?.target_audience),
        scheduled_at: campaignForm?.send_immediately ? null : campaignForm?.scheduled_at
      };

      const { data, error } = await whatsappService?.createBulkCampaign(campaignData);

      if (!error) {
        await loadInitialData();
        setShowCreateModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  const resetForm = () => {
    setCampaignForm({
      campaign_name: '',
      template_id: '',
      target_audience: {
        role: 'patient',
        active: true,
        age_min: null,
        age_max: null,
        conditions: []
      },
      scheduled_at: '',
      send_immediately: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-500/20';
      case 'sent': return 'text-blue-400 bg-blue-500/20';
      case 'delivered': return 'text-green-400 bg-green-500/20';
      case 'failed': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'sent': return <Send className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const calculateSuccessRate = (campaign) => {
    if (campaign?.total_recipients === 0) return 0;
    return Math.round((campaign?.delivered_count / campaign?.total_recipients) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-green-500/20 text-green-400">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Bulk Messaging</h2>
            <p className="text-gray-400">Create and manage bulk WhatsApp campaigns</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => {/* Import audience functionality */ }}
            className="px-4 py-2 text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500 rounded-xl transition-colors flex items-center space-x-2"
          >
            <Upload className="w-5 h-5" />
            <span>Import</span>
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create Campaign</span>
          </button>
        </div>
      </div>
      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
              <Send className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Total Campaigns</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {campaigns?.length || 0}
          </div>
          <div className="text-sm text-gray-400">All time</div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-green-500/20 text-green-400">
              <CheckCircle className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Messages Sent</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {campaigns?.reduce((total, campaign) => total + (campaign?.sent_count || 0), 0) || 0}
          </div>
          <div className="text-sm text-gray-400">Total delivered</div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400">
              <Target className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Avg. Success Rate</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {campaigns?.length ?
              Math.round(campaigns?.reduce((acc, campaign) => acc + calculateSuccessRate(campaign), 0) / campaigns?.length)
              : 0
            }%
          </div>
          <div className="text-sm text-gray-400">Delivery success</div>
        </div>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-yellow-500/20 text-yellow-400">
              <Clock className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Active Campaigns</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {campaigns?.filter(c => c?.status === 'pending')?.length || 0}
          </div>
          <div className="text-sm text-gray-400">Currently running</div>
        </div>
      </motion.div>
      {/* Campaigns List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden"
      >
        <div className="p-6 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Campaign History</h3>
        </div>

        <div className="divide-y divide-white/10">
          {campaigns?.length > 0 ? (
            campaigns?.map((campaign) => (
              <motion.div
                key={campaign?.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-6 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <h4 className="text-lg font-medium text-white">{campaign?.campaign_name}</h4>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(campaign?.status)}`}>
                        {getStatusIcon(campaign?.status)}
                        <span>{campaign?.status?.toUpperCase()}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-sm text-gray-400">Template</div>
                        <div className="text-white font-medium">{campaign?.template?.name}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400">Recipients</div>
                        <div className="text-white font-medium">{campaign?.total_recipients}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400">Sent</div>
                        <div className="text-white font-medium">{campaign?.sent_count}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400">Success Rate</div>
                        <div className="text-white font-medium">{calculateSuccessRate(campaign)}%</div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3">
                      <div className="flex justify-between text-sm text-gray-400 mb-1">
                        <span>Progress</span>
                        <span>{campaign?.sent_count} / {campaign?.total_recipients}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${campaign?.total_recipients ? (campaign?.sent_count / campaign?.total_recipients) * 100 : 0}%`
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>Created: {new Date(campaign.created_at)?.toLocaleDateString()}</span>
                      <span>By: {campaign?.creator?.full_name}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-6">
                    <button
                      onClick={() => setSelectedCampaign(campaign)}
                      className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors flex items-center space-x-1"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Details</span>
                    </button>

                    {campaign?.status === 'pending' && (
                      <button className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors flex items-center space-x-1">
                        <Pause className="w-4 h-4" />
                        <span>Pause</span>
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No campaigns yet</h3>
              <p className="text-gray-400 mb-6">Create your first bulk messaging campaign</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-colors"
              >
                Create Campaign
              </button>
            </div>
          )}
        </div>
      </motion.div>
      {/* Create Campaign Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gray-900 rounded-2xl border border-white/20 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Create Bulk Campaign</h3>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Campaign Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Campaign Name
                    </label>
                    <input
                      type="text"
                      value={campaignForm?.campaign_name}
                      onChange={(e) => setCampaignForm(prev => ({ ...prev, campaign_name: e?.target?.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="Enter campaign name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message Template
                    </label>
                    <select
                      value={campaignForm?.template_id}
                      onChange={(e) => setCampaignForm(prev => ({ ...prev, template_id: e?.target?.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    >
                      <option value="">Select template...</option>
                      {templates?.map(template => (
                        <option key={template?.id} value={template?.id}>
                          {template?.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Target Audience */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-4">
                    Target Audience
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {audienceOptions?.map((option) => {
                      const Icon = option?.icon;
                      return (
                        <button
                          key={option?.value}
                          onClick={() => setCampaignForm(prev => ({
                            ...prev,
                            target_audience: { ...prev?.target_audience, type: option?.value }
                          }))}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${campaignForm?.target_audience?.type === option?.value
                              ? 'border-blue-500 bg-blue-500/20' : 'border-white/10 bg-white/5 hover:bg-white/10'
                            }`}
                        >
                          <div className="flex items-center space-x-3 mb-2">
                            <Icon className="w-5 h-5 text-blue-400" />
                            <span className="font-medium text-white">{option?.label}</span>
                          </div>
                          <p className="text-sm text-gray-400">{option?.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Scheduling */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-4">
                    Campaign Scheduling
                  </label>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="scheduling"
                        checked={campaignForm?.send_immediately}
                        onChange={() => setCampaignForm(prev => ({ ...prev, send_immediately: true }))}
                        className="w-4 h-4 text-blue-500"
                      />
                      <span className="text-gray-300">Send immediately</span>
                    </label>

                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="scheduling"
                        checked={!campaignForm?.send_immediately}
                        onChange={() => setCampaignForm(prev => ({ ...prev, send_immediately: false }))}
                        className="w-4 h-4 text-blue-500"
                      />
                      <span className="text-gray-300">Schedule for later</span>
                    </label>

                    {!campaignForm?.send_immediately && (
                      <div className="ml-7">
                        <input
                          type="datetime-local"
                          value={campaignForm?.scheduled_at}
                          onChange={(e) => setCampaignForm(prev => ({ ...prev, scheduled_at: e?.target?.value }))}
                          className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Template Preview */}
                {campaignForm?.template_id && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h4 className="text-sm font-medium text-gray-300 mb-3">Message Preview</h4>
                    <div className="bg-green-600 text-white p-3 rounded-2xl rounded-bl-none max-w-xs">
                      {templates?.find(t => t?.id === campaignForm?.template_id)?.template_text}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-white/10 flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCampaign}
                  disabled={!campaignForm?.campaign_name || !campaignForm?.template_id}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
                >
                  <Send className="w-5 h-5" />
                  <span>Create Campaign</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Campaign Details Modal */}
      <AnimatePresence>
        {selectedCampaign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gray-900 rounded-2xl border border-white/20 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Campaign Details</h3>
                  <button
                    onClick={() => setSelectedCampaign(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Campaign Info */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-4">{selectedCampaign?.campaign_name}</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Status</span>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedCampaign?.status)}`}>
                            {selectedCampaign?.status?.toUpperCase()}
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Template</span>
                          <span className="text-white">{selectedCampaign?.template?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Created</span>
                          <span className="text-white">{new Date(selectedCampaign.created_at)?.toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Created By</span>
                          <span className="text-white">{selectedCampaign?.creator?.full_name}</span>
                        </div>
                      </div>
                    </div>

                    {/* Campaign Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 rounded-xl p-4">
                        <div className="text-2xl font-bold text-white">{selectedCampaign?.total_recipients}</div>
                        <div className="text-sm text-gray-400">Total Recipients</div>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4">
                        <div className="text-2xl font-bold text-white">{selectedCampaign?.sent_count}</div>
                        <div className="text-sm text-gray-400">Messages Sent</div>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4">
                        <div className="text-2xl font-bold text-white">{selectedCampaign?.delivered_count}</div>
                        <div className="text-sm text-gray-400">Delivered</div>
                      </div>
                      <div className="bg-white/5 rounded-xl p-4">
                        <div className="text-2xl font-bold text-white">{selectedCampaign?.failed_count}</div>
                        <div className="text-sm text-gray-400">Failed</div>
                      </div>
                    </div>
                  </div>

                  {/* Message Preview */}
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-4">Message Preview</h4>
                    <div className="bg-green-600 text-white p-4 rounded-2xl rounded-bl-none">
                      {selectedCampaign?.template?.template_text}
                    </div>

                    {/* Progress */}
                    <div className="mt-6">
                      <div className="flex justify-between text-sm text-gray-400 mb-2">
                        <span>Campaign Progress</span>
                        <span>{calculateSuccessRate(selectedCampaign)}% success</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3">
                        <div
                          className="bg-green-500 h-3 rounded-full transition-all duration-300"
                          style={{
                            width: `${selectedCampaign?.total_recipients ? (selectedCampaign?.sent_count / selectedCampaign?.total_recipients) * 100 : 0}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BulkMessaging;